import { ChangeDetectorRef, Component, computed, inject, output, Signal } from '@angular/core';
import { SignupV2Service } from '../../signup-v2.service';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AuthorizeService } from '../../../../shared/purchase/authorize/authorize.service';
import { ANetPayload, ANetResponse } from '../../../../shared/purchase/authorize/authorize.types';
import { AccountService } from '../../../../shared/account.service';
import { PaymentDataPayload } from '../../../../shared/types/account.types';
import { tap } from 'rxjs';
import { RouterLink } from '@angular/router';
import { handleImageError } from '../../../../shared/common/utils';
import { SectionLoaderComponent } from '../../../../shared/section-loader/section-loader.component';
import { LoaderSizeEnum } from '../../../../shared/section-loader/section-loader.types';
import { OnlyNumberDirective } from '../../../../shared/directives/only-number.directive';
import { NgbModule, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { NotificationService } from '../../../../shared/notification/notification.service';

@Component({
  selector: 'app-payment-step',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    RouterLink,
    SectionLoaderComponent,
    OnlyNumberDirective,
    NgbModule,
    NgbTooltip
  ],
  templateUrl: './payment-step.component.html',
  styleUrl: './payment-step.component.scss'
})
export class PaymentStepComponent {
  public svc = inject(SignupV2Service);
  private authorizeService = inject(AuthorizeService);
  private accountService = inject(AccountService);
  private notificationService = inject(NotificationService);
  public cdRef = inject(ChangeDetectorRef);

  // Output para comunicarse con el componente padre (opcional para payment-step)
  stepCompleted = output<void>();

  states = computed(() => this.svc.getStates());
  deliveryDayText = computed(() => this.svc.getDeliveryDayText());

  isProduceBoxesLoading: Signal<boolean> = computed(() => this.svc.getIsProduceBoxesLoading());
  isExtraSubscriptionsLoading: Signal<boolean> = computed(() => this.svc.getIsExtraSubscriptionsLoading());

  LoaderSizeEnum = LoaderSizeEnum;

  produceBoxes = computed(() => this.selectedProduceBoxes());
  extraSubscriptions = computed(() => this.selectedExtraSubscriptions());


  // Obtener los produce boxes seleccionados
  private selectedProduceBoxes() {
    const selectedIds = this.svc.getSelectedProduceBoxIds();
    const allBoxes = this.svc.getProduceBoxes();
    return allBoxes.filter(box => selectedIds.includes(box.id)).map(box => {
      const frequency = this.svc.getProduceBoxFrequency(box.id);
      return {
        ...box,
        frequency: frequency?.name,
      };
    });
  }

  // Obtener las suscripciones extra seleccionadas
  private selectedExtraSubscriptions() {
    const selectedIds = this.svc.getSelectedExtraSubscriptions();
    const allSubscriptions = this.svc.getExtraSubscriptions();
    return allSubscriptions.filter(sub => selectedIds.includes(sub.id)).map(sub => {
      const frequency = this.svc.getSubscriptionFrequency(sub.id);
      return {
        ...sub,
        frequency: frequency?.name,
      };
    });
  }

  // Método para remover un produce box
  removeProduceBox(boxId: number) {
    this.svc.toggleProduceBox(boxId);
  }

  // Método para remover una suscripción extra
  removeExtraSubscription(subscriptionId: number) {
    this.svc.toggleExtraSubscription(subscriptionId);
  }

  // Método legacy mantenido para compatibilidad
  get activeSubs(): string[] {
    const list: string[] = [];
    const map: Record<string, string> = { salmon: 'Wild Salmon Share', cheese: 'Artisan Cheese', coffee: 'Local Coffee Beans' };
    Object.entries(this.svc.getExtraSubscriptions()).forEach(([k, v]) => { if (v) list.push(map[k]); });
    return list;
  }
  invalid(name: string) { const c = this.svc.paymentForm.get(name); return !!c && c.invalid && (c.dirty || c.touched); }

  // --- Card brand detection and formatting ---
  cardBrand: 'visa' | 'mastercard' | 'amex' | 'discover' | 'unknown' = 'unknown';

  onImageError(event: Event) {
    handleImageError(event);
  }

  onCardNumberInput(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const digits = (input.value || '').replace(/\D+/g, '');
    this.cardBrand = this.detectBrand(digits);
    const formatted = this.formatCardNumber(digits, this.cardBrand);
    // Update input and form control without moving cursor management for simplicity
    input.value = formatted;
    this.svc.paymentForm.get('cardNumber')?.setValue(formatted, { emitEvent: false });
  }

  private detectBrand(num: string): 'visa' | 'mastercard' | 'amex' | 'discover' | 'unknown' {
    if (/^4/.test(num)) return 'visa';
    if (/^(5[1-5]|2(2[2-9][1-9]|2[3-9]\d|[3-6]\d{2}|7[01]\d|720))/.test(num)) return 'mastercard';
    if (/^(34|37)/.test(num)) return 'amex';
    if (/^(6011|65|64[4-9]|622(12[6-9]|1[3-9]\d|[2-8]\d{2}|9([01]\d|2[0-5])))/.test(num)) return 'discover';
    return 'unknown';
  }

  private formatCardNumber(num: string, brand: string): string {
    if (!num) return '';
    if (brand === 'amex') {
      return num
        .slice(0, 15)
        .replace(/^(\d{0,4})(\d{0,6})(\d{0,5}).*/, (m, g1, g2, g3) => [g1, g2, g3].filter(Boolean).join(' '));
    }
    // default: groups of 4 up to 19 chars with spaces
    return num
      .slice(0, 19)
      .replace(/(\d{4})(?=\d)/g, '$1 ') // insert spaces after each 4 digits when more digits follow
      .trim();
  }

  // Resolve card brand icon path (SVG in assets)
  cardBrandIcon(): string | null {
    switch (this.cardBrand) {
      case 'visa': return '/assets/images/credit-card/visa.svg';
      case 'mastercard': return '/assets/images/credit-card/master-card.svg';
      case 'amex': return '/assets/images/credit-card/american-express.svg';
      case 'discover': return '/assets/images/credit-card/discover.svg';
      default: return null;
    }
  }

  // --- Expiration date MM/YYYY formatting & input restrictions ---
  onExpKeyDown(ev: KeyboardEvent) {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (allowed.includes(ev.key)) return;
    if (/^[0-9]$/.test(ev.key)) return;
    ev.preventDefault();
  }

  onExpInput(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const digits = (input.value || '').replace(/\D+/g, '').slice(0, 6); // MMYYYY
    const mm = digits.slice(0, 2);
    const yyyy = digits.slice(2, 6);
    const formatted = yyyy ? `${mm}/${yyyy}` : mm;
    input.value = formatted;
    this.svc.paymentForm.get('exp')?.setValue(formatted, { emitEvent: false });
  }

  onExpPaste(ev: ClipboardEvent) {
    ev.preventDefault();
    const pasted = ev.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D+/g, '').slice(0, 6);
    const mm = digits.slice(0, 2);
    const yyyy = digits.slice(2, 6);
    const input = ev.target as HTMLInputElement;
    const formatted = yyyy ? `${mm}/${yyyy}` : mm;
    input.value = formatted;
    this.svc.paymentForm.get('exp')?.setValue(formatted, { emitEvent: false });
  }

  // --- Accept.js Integration ---
  sendDataToANet() {
    if (!this.svc.paymentForm.valid) return;
    const formValue = this.svc.paymentForm.value;
    if (!formValue.terms) {
      this.notificationService.show({ text: 'Please agree to the terms and conditions', type: 'warning' });
      return;
    };
    const [month, year] = formValue.exp?.split('/') || ['', ''];

    const ANetData: ANetPayload = {
      cardCode: formValue.cvc || '',
      cardNumber: formValue.cardNumber?.replace(/\s/g, '') || '',
      year,
      month
    };

    this.authorizeService.sendPaymentDataToAnet(ANetData, (response: ANetResponse) =>
      this.addPaymentMethod(response)
    );
  }

  private addPaymentMethod(opaqueData: ANetResponse) {
    const payload = this.setUpPaymentMethodPayload(opaqueData);
    this.accountService
      .addPaymentMethod(payload)
      .pipe(
        tap(res => {
          if (res?.data) {
            // Notificar al servicio que el registro del metodo de pago fue exitoso
            setTimeout(() => {
              this.svc.onPaymentMethodAdded();
            }, 1);
          }
        })
      )
      .subscribe();
  }

  private setUpPaymentMethodPayload(opaqueData: ANetResponse): PaymentDataPayload {
    const formValue = this.svc.paymentForm.value;
    const cardNumber = formValue.cardNumber?.replace(/\s/g, '') || '';
    const cardLastNumbers = cardNumber.substring(cardNumber.length - 4, cardNumber.length);

    const payload: PaymentDataPayload = {
      card: {
        firstName: formValue.billingName?.split(' ')[0] || '',
        lastName: formValue.billingName?.split(' ').slice(1).join(' ') || '',
        expirationDate: formValue.exp?.split('/').reverse().join('-') || '',
        number: cardLastNumbers,
      },
      opaqueData
    };

    // Only include billingAddress if it's different from delivery address
    payload.billingAddress = {
      additional: formValue.billingAddressDifferent ? formValue.billingAddress2 || '' : undefined,
      city: formValue.billingAddressDifferent ? formValue.billingCity || '' : undefined,
      stateCode: formValue.billingAddressDifferent ? formValue.billingState || '' : undefined,
      street: formValue.billingAddressDifferent ? formValue.billingAddress1 || '' : undefined,
      zip: formValue.billingAddressDifferent ? formValue.billingZip || '' : undefined
    };

    return payload;
  }
}
